/**
 * LocationPickerEditor
 * Configuration for location-picker (map integration)
 * Uses libraries: Leaflet (lightweight map library)
 */

import React, { useCallback } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../../registry/PropertyEditor.types';

interface LocationPickerConfig {
  mapProvider?: 'leaflet' | 'google-maps';
  defaultLat?: number;
  defaultLng?: number;
  defaultZoom?: number;
  maxZoom?: number;
  minZoom?: number;
  showSearch?: boolean;
  showGeolocation?: boolean;
}

function renderLocationPickerEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;
  const config = (control.config || {}) as LocationPickerConfig;

  const mapProvider = config.mapProvider || 'leaflet';
  const defaultLat = config.defaultLat ?? 0;
  const defaultLng = config.defaultLng ?? 0;
  const defaultZoom = config.defaultZoom ?? 12;
  const maxZoom = config.maxZoom ?? 18;
  const minZoom = config.minZoom ?? 2;
  const showSearch = config.showSearch ?? true;
  const showGeolocation = config.showGeolocation ?? true;

  const handleLatChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onConfigChange('defaultLat', Number(e.target.value));
    },
    [onConfigChange],
  );

  const handleLngChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onConfigChange('defaultLng', Number(e.target.value));
    },
    [onConfigChange],
  );

  const handleZoomChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onConfigChange('defaultZoom', Number(e.target.value));
    },
    [onConfigChange],
  );

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Location Picker Configuration</h4>

      <div className="af-properties-tab__group">
        <label htmlFor="map-provider-select" className="af-properties-tab__label">
          Map Provider
        </label>
        <select
          id="map-provider-select"
          value={mapProvider}
          onChange={(e) => onConfigChange('mapProvider', e.target.value)}
          className="af-properties-tab__select"
        >
          <option value="leaflet">Leaflet (OSM)</option>
          <option value="google-maps">Google Maps</option>
        </select>
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="default-lat-input" className="af-properties-tab__label">
          Default Latitude
        </label>
        <input
          id="default-lat-input"
          type="number"
          value={defaultLat}
          onChange={handleLatChange}
          min="-90"
          max="90"
          step="0.0001"
          className="af-properties-tab__input"
          aria-label="Default latitude"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="default-lng-input" className="af-properties-tab__label">
          Default Longitude
        </label>
        <input
          id="default-lng-input"
          type="number"
          value={defaultLng}
          onChange={handleLngChange}
          min="-180"
          max="180"
          step="0.0001"
          className="af-properties-tab__input"
          aria-label="Default longitude"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="default-zoom-input" className="af-properties-tab__label">
          Default Zoom Level
        </label>
        <input
          id="default-zoom-input"
          type="number"
          value={defaultZoom}
          onChange={handleZoomChange}
          min={minZoom}
          max={maxZoom}
          className="af-properties-tab__input"
          aria-label="Default zoom level"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="min-zoom-input" className="af-properties-tab__label">
          Minimum Zoom
        </label>
        <input
          id="min-zoom-input"
          type="number"
          value={minZoom}
          onChange={(e) => onConfigChange('minZoom', Number(e.target.value))}
          min="0"
          max="28"
          className="af-properties-tab__input"
          aria-label="Minimum zoom"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="max-zoom-input" className="af-properties-tab__label">
          Maximum Zoom
        </label>
        <input
          id="max-zoom-input"
          type="number"
          value={maxZoom}
          onChange={(e) => onConfigChange('maxZoom', Number(e.target.value))}
          min="0"
          max="28"
          className="af-properties-tab__input"
          aria-label="Maximum zoom"
        />
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={showSearch}
            onChange={(e) => onConfigChange('showSearch', e.target.checked)}
          />
          Show Location Search
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={showGeolocation}
            onChange={(e) => onConfigChange('showGeolocation', e.target.checked)}
          />
          Show Geolocation Button
        </label>
      </div>
    </div>
  );
}

export const LocationPickerEditor: PropertyEditor = {
  type: 'location-picker',
  label: 'Location Picker',
  description: 'Configure map-based location selection',
  render: renderLocationPickerEditor,
};
